import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { useCountdown } from './useCountdown';
import { formatReleaseDay } from './presaveDates';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole panel; each part uses its own slice of it */
import styles from './PresavePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PresaveCountdownRowProps {
  releaseDate: string;
  /** An unpublished page collects nothing, so the clock says so. */
  isDraft: boolean;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * The release moment, as a clock rather than a sentence.
 *
 * Days:hours:minutes while there is still runway, hours:minutes:seconds on the
 * last day — the artist watching the final hour wants the seconds, and nobody
 * watching a fortnight out wants them.
 */
const PresaveCountdownRowComponent: React.FC<PresaveCountdownRowProps> = ({ releaseDate, isDraft }) => {
  const intl = useIntl();
  const countdown = useCountdown(releaseDate);

  const units = useMemo(() => {
    if (countdown.isFinalDay) {
      return [
        {
          key: 'hrs',
          value: pad(countdown.hours),
          label: intl.formatMessage({ id: 'app.sml.presave.panel.unit.hours', defaultMessage: 'hrs' }),
        },
        {
          key: 'min',
          value: pad(countdown.minutes),
          label: intl.formatMessage({ id: 'app.sml.presave.panel.unit.minutes', defaultMessage: 'min' }),
        },
        {
          key: 'sec',
          value: pad(countdown.seconds),
          label: intl.formatMessage({ id: 'app.sml.presave.panel.unit.seconds', defaultMessage: 'sec' }),
        },
      ];
    }
    return [
      {
        key: 'days',
        value: pad(countdown.days),
        label: intl.formatMessage({ id: 'app.sml.presave.panel.unit.days', defaultMessage: 'days' }),
      },
      {
        key: 'hrs',
        value: pad(countdown.hours),
        label: intl.formatMessage({ id: 'app.sml.presave.panel.unit.hours', defaultMessage: 'hrs' }),
      },
      {
        key: 'min',
        value: pad(countdown.minutes),
        label: intl.formatMessage({ id: 'app.sml.presave.panel.unit.minutes', defaultMessage: 'min' }),
      },
    ];
  }, [countdown, intl]);

  return (
    <div className={styles.countdown}>
      <div className={styles.units}>
        {units.map((unit, index) => (
          <React.Fragment key={unit.key}>
            {index > 0 && <span className={styles.unitSeparator}>:</span>}
            <span className={styles.unit}>
              <span className={styles.unitValue}>{unit.value}</span>
              <span className={styles.unitLabel}>{unit.label}</span>
            </span>
          </React.Fragment>
        ))}
      </div>
      <div className={styles.when}>
        <span className={styles.whenDate}>{formatReleaseDay(intl, releaseDate)}</span>
        <span className={styles.whenNote}>
          {isDraft
            ? intl.formatMessage({
                id: 'app.sml.presave.panel.release_day_draft',
                defaultMessage: 'Release day · not counting down yet',
              })
            : intl.formatMessage({ id: 'app.sml.presave.panel.release_day', defaultMessage: 'Release day' })}
        </span>
      </div>
    </div>
  );
};

export const PresaveCountdownRow = TypedMemo(PresaveCountdownRowComponent);
